/**
 * Search Cache Utility
 * 
 * Manages client-side caching of search results to improve performance
 * when users navigate back to the Find page.
 * 
 * Features:
 * - Caches search results in sessionStorage (survives navigation, cleared on tab close)
 * - Smart cache invalidation when items/boxes are modified
 * - Preserves UI state (search query, scroll position, filters)
 */

const CACHE_KEY_PREFIX = 'cubxly_search_cache_';
const CACHE_VERSION_KEY = 'cubxly_search_cache_version';
const CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Generate a cache key for a search query
 */
const getCacheKey = (searchQuery) => {
  return `${CACHE_KEY_PREFIX}${searchQuery.toLowerCase().trim()}`;
};

/**
 * Get current cache version (used for invalidation)
 */
export const getCacheVersion = () => {
  try {
    return sessionStorage.getItem(CACHE_VERSION_KEY) || '1';
  } catch (error) {
    console.warn('searchCache: Failed to get cache version:', error);
    return '1';
  }
};

/**
 * Invalidate entire search cache by clearing all cached searches
 */
export const invalidateSearchCache = () => {
  try {
    // Clear all search cache entries from sessionStorage
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Bump version for good measure
    const currentVersion = parseInt(getCacheVersion(), 10);
    const newVersion = (currentVersion + 1).toString();
    sessionStorage.setItem(CACHE_VERSION_KEY, newVersion);
    
    console.log(`searchCache: ⚠️ Cache invalidated. Cleared ${keysToRemove.length} cached searches. New version: ${newVersion}`);
    return newVersion;
  } catch (error) {
    console.warn('searchCache: Failed to invalidate cache:', error);
    return getCacheVersion();
  }
};

/**
 * Save search results to cache
 * 
 * @param {string} searchQuery - The search query string
 * @param {Object} searchData - Search results data
 * @param {Array} searchData.items - Array of item results
 * @param {Array} searchData.boxes - Array of box results
 * @param {Object} searchData.searchMetrics - Search performance metrics
 * @param {string} searchData.searchTier - Search tier used (keyword, hybrid, etc.)
 * @param {number} searchData.searchTime - Search execution time in ms
 */
export const saveSearchToCache = (searchQuery, searchData) => {
  if (!searchQuery || !searchQuery.trim()) {
    console.warn('searchCache: Cannot save empty search query');
    return;
  }

  try {
    const cacheKey = getCacheKey(searchQuery);
    const cacheEntry = {
      query: searchQuery,
      items: searchData.items || [],
      boxes: searchData.boxes || [],
      searchMetrics: searchData.searchMetrics || {},
      searchTier: searchData.searchTier || 'keyword',
      searchTime: searchData.searchTime || null,
      timestamp: Date.now(),
      version: getCacheVersion()
    };

    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    console.log(`searchCache: Saved results for "${searchQuery}" (${cacheEntry.items.length} items, ${cacheEntry.boxes.length} boxes)`);
  } catch (error) {
    console.warn('searchCache: Failed to save search results:', error);
    // Storage quota exceeded - clear old entries
    if (error.name === 'QuotaExceededError') {
      clearOldCacheEntries();
    }
  }
};

/**
 * Get cached search results
 * 
 * @param {string} searchQuery - The search query string
 * @returns {Object|null} Cached search data or null if not found/expired
 */
export const getCachedSearch = (searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return null;
  }

  try {
    const cacheKey = getCacheKey(searchQuery);
    const cachedData = sessionStorage.getItem(cacheKey);
    
    if (!cachedData) {
      console.log(`searchCache: No cache found for "${searchQuery}"`);
      return null;
    }

    const cacheEntry = JSON.parse(cachedData);
    const currentVersion = getCacheVersion();
    const age = Date.now() - cacheEntry.timestamp;

    // Check if cache is still valid
    if (cacheEntry.version !== currentVersion) {
      console.log(`searchCache: Cache invalidated for "${searchQuery}" (version mismatch)`);
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    if (age > CACHE_MAX_AGE_MS) {
      console.log(`searchCache: Cache expired for "${searchQuery}" (age: ${Math.round(age / 1000)}s)`);
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    console.log(`searchCache: ✅ Cache hit for "${searchQuery}" (age: ${Math.round(age / 1000)}s, ${cacheEntry.items.length} items)`);
    return cacheEntry;
  } catch (error) {
    console.warn('searchCache: Failed to retrieve cached search:', error);
    return null;
  }
};

/**
 * Clear old cache entries to free up storage space
 */
const clearOldCacheEntries = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const data = JSON.parse(sessionStorage.getItem(key));
          const age = Date.now() - data.timestamp;
          
          // Remove entries older than 15 minutes
          if (age > CACHE_MAX_AGE_MS / 2) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid entry, mark for removal
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    console.log(`searchCache: Cleared ${keysToRemove.length} old cache entries`);
  } catch (error) {
    console.warn('searchCache: Failed to clear old entries:', error);
  }
};

/**
 * Clear specific search from cache
 * 
 * @param {string} searchQuery - The search query to clear
 */
export const clearSearchCache = (searchQuery) => {
  if (!searchQuery || !searchQuery.trim()) {
    return;
  }

  try {
    const cacheKey = getCacheKey(searchQuery);
    sessionStorage.removeItem(cacheKey);
    console.log(`searchCache: Cleared cache for "${searchQuery}"`);
  } catch (error) {
    console.warn('searchCache: Failed to clear search cache:', error);
  }
};

/**
 * Clear all search cache entries
 */
export const clearAllSearchCache = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    console.log(`searchCache: Cleared all search cache (${keysToRemove.length} entries)`);
  } catch (error) {
    console.warn('searchCache: Failed to clear all cache:', error);
  }
};

/**
 * Get cache statistics (for debugging)
 */
export const getCacheStats = () => {
  try {
    const stats = {
      totalEntries: 0,
      totalSize: 0,
      entries: []
    };

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        const data = sessionStorage.getItem(key);
        const entry = JSON.parse(data);
        const age = Date.now() - entry.timestamp;

        stats.totalEntries++;
        stats.totalSize += data.length;
        stats.entries.push({
          query: entry.query,
          items: entry.items.length,
          boxes: entry.boxes.length,
          age: Math.round(age / 1000),
          size: data.length
        });
      }
    }

    return stats;
  } catch (error) {
    console.warn('searchCache: Failed to get cache stats:', error);
    return { totalEntries: 0, totalSize: 0, entries: [] };
  }
};
